/**
 * Appostle MCP server, served over HTTP at /mcp/appostle.
 *
 * Exposes Appostle harness tools that agents use to coordinate with each other
 * and act on `>` directives:
 * `handoff`, `report`, `talkback`, `chain`, `learn`, `write_plan`, `todo`,
 * `todo_list`, plus cross-host tools when available.
 *
 * Unlike the old in-process SDK path, this server is
 * transport-agnostic — it uses the @modelcontextprotocol/sdk McpServer so any
 * provider (Claude, OpenCode, Codex) can reach it over HTTP. The handler logic
 * is identical; only the registration layer changed.
 *
 * callerAgentId is read from the ?callerAgentId=<id> query param when the
 * MCP session is initialized (same pattern as /mcp/agents).
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { randomUUID } from "node:crypto";
import type { Logger } from "pino";
import { z } from "zod";
import type {
  AgentManager,
  AddTodoFromAgent,
  CompleteTodoFromAgent,
  ListTodosForAgent,
} from "./agent-manager.js";
import type { AgentStorage } from "./agent-storage.js";
import type { ProviderSnapshotManager } from "./provider-snapshot-manager.js";
import type { ProviderSnapshotEntry } from "./agent-sdk-types.js";
import type { AgentTimelineItem } from "./agent-sdk-types.js";
import { AGENT_PROVIDER_DEFINITIONS } from "./provider-manifest.js";
import {
  escapeAppostleSystemAttr,
  sendPromptToAgent,
  setupFinishNotification,
  startAgentRun,
} from "./mcp-shared.js";
import { spawnHandoffChild } from "./handoff-service.js";
import {
  WRITE_PLAN_DESCRIPTION,
  writePlanSchema,
  buildWritePlanHandler,
} from "./write-plan-tool.js";
import type { KeyPair } from "@appostle/relay/e2ee";
import { APPOSTLE_DIRECTIVES } from "../../shared/directives.js";
import {
  createAuthServerLookup,
  dialHostExec,
  dialHostTransfer,
} from "../cross-host/relay-dialer.js";
import {
  type CrossHostRouteProgressEvent,
  resolveCrossHostRoute,
  runSshExec,
  runSshTransfer,
} from "../cross-host/route-resolver.js";

/**
 * Everything the `cross_hosts_terminal` tool needs to dial a sibling host over
 * the relay and run a command there. Pre-resolved at MCP-server-build time so
 * the tool only registers (and lists siblings in its description) when at least
 * one reachable sibling exists. When absent / empty, the tool is not offered.
 */
export interface CrossHostExecOptions {
  authServerUrl: string;
  daemonToken: string;
  /** This daemon's persistent client-identity keypair (enrolled as a device). */
  clientIdentity: KeyPair;
  clientIdentityPubkeyB64: string;
  /** Reachable sibling hosts (owner-accessible, online), fetched at build time. */
  reachableSiblings: Array<{
    serverId: string;
    name: string;
    icon?: string | null;
    directEndpoints?: Array<{ endpoint: string; source: string; observedAt: string }>;
    organization?: { id: string; name: string; slug: string | null; logo?: string | null } | null;
    owner?: { userId: string; handle: string | null; image?: string | null } | null;
  }>;
}

export interface AppostleHttpMcpOptions {
  agentManager: AgentManager;
  appostleHome: string;
  logger: Logger;
  agentStorage?: AgentStorage;
  providerSnapshotManager?: ProviderSnapshotManager;
  addTodoFromAgent?: AddTodoFromAgent | null;
  listTodosForAgent?: ListTodosForAgent | null;
  completeTodoFromAgent?: CompleteTodoFromAgent | null;
  /**
   * Cross-host command exec. When present with at least one reachable sibling,
   * registers the `cross_hosts_terminal` tool. Pre-resolved at build time so
   * the tool is silent when the owner has no reachable sibling host.
   */
  crossHostExec?: CrossHostExecOptions | null;
  /**
   * Fires after `handoff` spawns a child agent. Lets bootstrap.ts call into
   * the auth-server (via `createOwnAgentTabInheritingStack`) so the child's
   * tab materialises immediately in the same stack as the parent — instead
   * of waiting for the next workspace tab-backfill cycle to discover it.
   *
   * Fire-and-forget on the handoff handler's side — errors are caught and
   * logged so a transient auth-server hiccup doesn't break handoff.
   */
  onHandoffSpawned?: (params: {
    parentAgentId: string;
    parentAgentCwd: string;
    parentAgentOwnerUserId: string;
    childAgentId: string;
    childAgentTitle: string | null;
    sharees: Array<{ userId: string }>;
  }) => Promise<void>;
}

/**
 * Build a dynamic description of available providers and models from the live
 * provider snapshot. Falls back to static provider definitions when no snapshot
 * is available.
 */
function buildProviderModelCatalog(snapshotEntries: ProviderSnapshotEntry[] | undefined): string {
  const providerDefs = AGENT_PROVIDER_DEFINITIONS;
  const lines: string[] = [];

  for (const def of providerDefs) {
    const entry = snapshotEntries?.find((e) => e.provider === def.id);
    const models = entry?.models;
    if (models && models.length > 0) {
      const modelIds = models.map((m) => m.id).join(", ");
      lines.push(`- \`${def.id}\` (${def.label}): ${modelIds}`);
    } else if (entry?.status === "ready") {
      lines.push(`- \`${def.id}\` (${def.label}): available (models not enumerated)`);
    } else {
      lines.push(`- \`${def.id}\` (${def.label}): ${entry?.status ?? "not configured"}`);
    }
  }
  return lines.join("\n");
}

function withChainContract(brief: string): string {
  return [
    "## Chain mode engaged",
    "",
    "You are now an interrogator-shepherd for this task. Two phases:",
    "",
    "**Phase 1 — Scoping.** Before any code or sub-handoffs:",
    "1. Read the brief at the bottom.",
    "2. Identify the top 2-4 unknowns that would change your approach.",
    "3. Use `AskUserQuestion` to ask the user those questions. Wait for answers.",
    "4. Once scoped, state your plan in one short paragraph.",
    "",
    "**Phase 2 — Delegation.** Once the user has greenlit the plan:",
    "1. Break the work into sub-tasks.",
    "2. Delegate each via the `handoff` tool — each becomes a disciple session.",
    "3. Use `talkback` to follow up with a disciple without spawning a new one.",
    "4. When all sub-tasks complete, summarize the outcome for the user.",
    "",
    "Do NOT skip Phase 1. Do NOT start editing files in this session — delegate",
    "everything. You are coordinating, not executing.",
    "",
    "## Brief",
    "",
    brief,
  ].join("\n");
}

type CrossHostToolTarget = CrossHostExecOptions["reachableSiblings"][number];

interface CrossHostProgressState {
  operation: "terminal" | "transfer";
  target: CrossHostToolTarget;
  command?: string;
  cwd?: string;
  steps: string[];
  route?: string;
  routeReason?: string;
  exit?: string;
  stdout?: string;
  stderr?: string;
}

const CROSS_HOST_OUTPUT_CAP = 100_000;

function capCrossHostOutput(text: string): string {
  if (text.length <= CROSS_HOST_OUTPUT_CAP) return text;
  return `${text.slice(0, CROSS_HOST_OUTPUT_CAP)}\n… (truncated, ${text.length} chars total)`;
}

function buildCrossHostProgressOutput(state: CrossHostProgressState): Record<string, unknown> {
  return {
    host: state.target.name,
    serverId: state.target.serverId,
    icon: state.target.icon ?? null,
    organization: state.target.organization?.name ?? null,
    organizationLogo: state.target.organization?.logo ?? null,
    owner: state.target.owner?.handle ?? state.target.owner?.userId ?? null,
    ownerImage: state.target.owner?.image ?? null,
    route: state.route ?? null,
    routeReason: state.routeReason ?? null,
    exit: state.exit ?? null,
    steps: state.steps,
    stdout: state.stdout ?? null,
    stderr: state.stderr ?? null,
  };
}

function emitCrossHostProgress(
  agentManager: AgentManager,
  callerAgentId: string,
  callId: string,
  state: CrossHostProgressState,
  status: "running" | "completed" | "failed" = "running",
  error: unknown = null,
): void {
  const base = {
    type: "tool_call",
    callId,
    name: "mcp__appostle__cross_hosts_terminal",
    metadata: {
      appostleSynthetic: true,
      crossHostOperation: state.operation,
    },
    detail: {
      type: "unknown",
      input: {
        host: state.target.name,
        command: state.command,
        cwd: state.cwd,
      },
      output: buildCrossHostProgressOutput(state),
    },
  } satisfies Omit<Extract<AgentTimelineItem, { type: "tool_call" }>, "status" | "error">;
  const item: AgentTimelineItem =
    status === "failed"
      ? {
          ...base,
          status: "failed",
          error: error ?? { message: "Cross-host command failed" },
        }
      : {
          ...base,
          status,
          error: null,
        };
  void agentManager.emitLiveTimelineItem(callerAgentId, item);
}

function formatCrossHostIdentityLines(target: CrossHostToolTarget): string[] {
  return [
    `host: ${target.name} (${target.serverId})`,
    target.icon ? `icon: ${target.icon}` : undefined,
    target.organization?.name ? `organization: ${target.organization.name}` : undefined,
    target.organization?.logo ? `organization_logo: ${target.organization.logo}` : undefined,
    target.owner?.handle || target.owner?.userId
      ? `owner: ${target.owner.handle ?? target.owner.userId}`
      : undefined,
    target.owner?.image ? `owner_image: ${target.owner.image}` : undefined,
  ].filter((line): line is string => Boolean(line));
}

export function createAppostleHttpMcpServer(
  callerAgentId: string,
  options: AppostleHttpMcpOptions,
): McpServer {
  const {
    agentManager,
    appostleHome,
    logger,
    agentStorage,
    providerSnapshotManager,
    addTodoFromAgent,
    listTodosForAgent,
    completeTodoFromAgent,
    onHandoffSpawned,
    crossHostExec,
  } = options;
  const log = logger.child({ module: "appostle-http-mcp-server", callerAgentId });
  const handoffDirective = APPOSTLE_DIRECTIVES.find((directive) => directive.id === "handoff");

  // Build dynamic catalogs from the live provider snapshot (if available)
  // so agents see the actual providers and models available on this host.
  const callerSnapshot = agentManager.getAgentSnapshot(callerAgentId);
  const snapshotEntries = providerSnapshotManager?.getSnapshot(callerSnapshot?.cwd);
  const providerCatalog = AGENT_PROVIDER_DEFINITIONS.map((def) => {
    const entry = snapshotEntries?.find((e) => e.provider === def.id);
    const status = entry?.status ?? "not configured";
    return `- \`${def.id}\` (${def.label}): ${def.description} [${status}]`;
  }).join("\n");
  const modelCatalog = buildProviderModelCatalog(snapshotEntries);

  const server = new McpServer({
    name: "appostle",
    version: "0.1.0",
  });

  // ── handoff ───────────────────────────────────────────────────────────────
  server.tool(
    "handoff",
    [
      "Directive target for user messages starting with `>handoff`.",
      handoffDirective?.description ??
        "Spawn one independent Appostle agent session and give it a self-contained task.",
      "Returns immediately with the new agent ID; do not wait for completion.",
      "",
      "Call this as the first action for `>handoff ...`.",
    ].join("\n"),
    {
      task: z
        .string()
        .min(1)
        .describe(
          [
            "Self-contained machine-to-machine task brief for the child session.",
            "The child cannot see the parent conversation.",
            "Include goal, paths, constraints, and decisions already made.",
            "Keep it dense: usually 3-15 short lines.",
          ].join("\n"),
        ),
      title: z
        .string()
        .optional()
        .describe("Short title for the session tab (auto-generated if omitted)."),
      provider: z
        .string()
        .optional()
        .describe(
          [
            "Target provider. Omit to inherit the parent's provider.",
            "Only set when the user explicitly asks for another provider.",
            "",
            "Available providers on this host:",
            providerCatalog,
          ].join("\n"),
        ),
      model: z
        .string()
        .optional()
        .describe(
          [
            "Target model. Omit to inherit the parent's model.",
            "",
            "Claude aliases resolve to the newest model of that family: `haiku` for",
            "cheap/fast, `sonnet` for normal coding, `opus`/`fable` for deep",
            "architecture/debugging. When the user asks for a SPECIFIC version",
            "(e.g. Opus 4.8), pass the exact model ID from the list below —",
            "unknown Claude model strings are rejected.",
            "",
            "Other providers: pass the model ID directly.",
            "",
            "Available models per provider:",
            modelCatalog,
          ].join("\n"),
        ),
    },
    async (args) => {
      try {
        const childAgent = await spawnHandoffChild({
          agentManager,
          agentStorage,
          appostleHome,
          logger: log,
          callerAgentId,
          task: args.task,
          title: args.title,
          provider: args.provider,
          model: args.model,
          onHandoffSpawned,
        });
        return {
          content: [
            {
              type: "text",
              text: `Handoff spawned as ${childAgent.id} and is visible as a tab. Do not mention the child agent ID to the user unless they explicitly ask.`,
            },
          ],
        };
      } catch (err) {
        log.error({ err }, "handoff: createAgent failed");
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `handoff failed: could not spawn session — ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        };
      }
    },
  );

  // ── report ────────────────────────────────────────────────────────────────
  server.tool(
    "report",
    '**ALWAYS call this tool as your final action before finishing your task, to deliver your findings to the parent agent.** Without this call, the parent only receives a content-free "finished" notification and is blind to whatever you did or learned. The first sentence of your report should be a one-line verdict; the rest can fill in detail. Also call this tool any time the user explicitly types `>report` mid-task to send an interim update — the auto-return on session end is independent from manual reports, and you can call it any number of times over the session\'s lifetime. Only works in sessions that were spawned via `handoff`; errors otherwise.',
    {
      message: z
        .string()
        .min(1)
        .describe(
          "The curated report to deliver to the parent session. Self-contained: the parent does not see this conversation's timeline.",
        ),
    },
    async (args) => {
      const childAgent = agentManager.getAgentSnapshot(callerAgentId);
      if (!childAgent) {
        return {
          isError: true,
          content: [
            { type: "text", text: `Caller agent ${callerAgentId} not found in agent manager` },
          ],
        };
      }

      const parentAgentId = childAgent.labels?.["appostle.parent-agent-id"];
      if (!parentAgentId) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "This session was not spawned via `handoff`, so there is no parent to report to.",
            },
          ],
        };
      }

      const parentAgent = agentManager.getAgentSnapshot(parentAgentId);
      if (!parentAgent) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `Parent agent ${parentAgentId} is no longer available.`,
            },
          ],
        };
      }

      const childTitle = childAgent.config?.title ?? callerAgentId;
      const trimmedMessage = args.message.trim();
      const prompt = `<appostle-system kind="handoff-report" agent-id="${escapeAppostleSystemAttr(callerAgentId)}" title="${escapeAppostleSystemAttr(childTitle)}">\nHandoff "${childTitle}" reported back:\n\n<display>\n${trimmedMessage}\n</display>\n</appostle-system>`;

      try {
        agentManager.recordUserMessage(parentAgentId, prompt);
        startAgentRun(agentManager, parentAgentId, prompt, log, {
          replaceRunning: true,
        });
      } catch (err) {
        log.error({ err, parentAgentId }, "report: failed to inject prompt into parent");
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `report failed: could not wake parent — ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        };
      }

      return {
        content: [
          {
            type: "text",
            text: `Reported to parent ${parentAgentId}.`,
          },
        ],
      };
    },
  );

  // ── talkback ──────────────────────────────────────────────────────────────
  server.tool(
    "talkback",
    [
      "Send a follow-up message into a finished disciple's session. Use this",
      "when the user types `>talkback <id> <msg>` or when you (the shepherd)",
      "want to ask one clarifying thing of a disciple that already reported",
      "back — instead of spawning a whole new disciple via `handoff`.",
      "",
      "The disciple is re-woken with your message as a new user turn. It will",
      "process and eventually report back again or finish, producing a new",
      "card in your timeline. Errors if the disciple session is archived.",
    ].join("\n"),
    {
      agent_id: z
        .string()
        .min(1)
        .describe(
          [
            "The disciple's agent ID. Copy it from the `handoff-report` or",
            "`handoff-finish` envelope that appeared in your timeline. The",
            "target must be one of YOUR disciples (spawned via your `handoff`",
            "calls) — talkback into unrelated agents is rejected.",
          ].join(" "),
        ),
      message: z
        .string()
        .min(1)
        .describe(
          [
            "The follow-up. Dense, direct, machine-to-machine — same style as",
            "the original task brief. Lead with what you want; the disciple",
            "already has all the conversation history. No motivational framing.",
          ].join(" "),
        ),
    },
    async (args) => {
      if (!agentStorage) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "talkback: the daemon is not configured with agent storage.",
            },
          ],
        };
      }

      const callerAgent = agentManager.getAgentSnapshot(callerAgentId);
      if (!callerAgent) {
        return {
          isError: true,
          content: [{ type: "text", text: `Caller agent ${callerAgentId} not found` }],
        };
      }

      const targetId = args.agent_id.trim();
      const targetAgent = agentManager.getAgentSnapshot(targetId);
      if (!targetAgent) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `Disciple ${targetId} not found. Pass the agent ID from a handoff-report or handoff-finish envelope in your timeline.`,
            },
          ],
        };
      }

      const targetParentId = targetAgent.labels?.["appostle.parent-agent-id"];
      if (targetParentId !== callerAgentId) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `Disciple ${targetId} is not your disciple — talkback can only address sessions you spawned via handoff.`,
            },
          ],
        };
      }

      if (targetAgent.lifecycle === "closed") {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `Disciple ${targetId} session is archived and cannot be resumed. Spawn a new one with handoff.`,
            },
          ],
        };
      }

      const shepherdTitle = callerAgent.config?.title ?? callerAgentId;
      const trimmedMessage = args.message.trim();
      const envelope = `<appostle-system kind="talkback" agent-id="${escapeAppostleSystemAttr(callerAgentId)}" title="${escapeAppostleSystemAttr(shepherdTitle)}">\nShepherd "${shepherdTitle}" followed up:\n\n<display>\n${trimmedMessage}\n</display>\n</appostle-system>`;

      try {
        await sendPromptToAgent({
          agentManager,
          agentStorage,
          agentId: targetId,
          userMessageText: envelope,
          prompt: envelope,
          logger: log,
        });
      } catch (err) {
        log.error({ err, targetId }, "talkback: failed to inject prompt into disciple");
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `talkback failed: could not re-wake disciple — ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        };
      }

      try {
        setupFinishNotification({
          agentManager,
          agentStorage,
          childAgentId: targetId,
          callerAgentId,
          logger: log,
        });
      } catch (err) {
        log.error({ err, targetId }, "talkback: failed to re-arm finish notification");
      }

      return {
        content: [
          {
            type: "text",
            text: `Talkback sent to ${targetId}. The disciple has been re-woken and will respond.`,
          },
        ],
      };
    },
  );

  // ── chain ─────────────────────────────────────────────────────────────────
  server.tool(
    "chain",
    [
      "Engage chain mode in THIS session — do not spawn a new one. Use",
      "this when the user types `>chain <brief>` for a larger task that",
      "needs scoping conversation before any work begins.",
      "",
      "Effect: this tool returns a two-phase contract you must follow for the",
      "rest of the session. Phase 1 = interrogate the user via AskUserQuestion",
      "to nail down approach. Phase 2 = delegate sub-tasks via `handoff` and",
      "coordinate via `talkback`. You become a shepherd for sub-disciples.",
      "",
      "Different from `handoff`: handoff fans out to a new session and the",
      "current session stays passive. chain stays in the current session",
      "and switches the current session into interrogator-shepherd mode.",
    ].join("\n"),
    {
      brief: z
        .string()
        .min(1)
        .describe(
          [
            "Seed context for chain mode. What you know so far —",
            "constraints, file paths, what the user said. The interrogation",
            "contract will use this plus the user's answers to form sub-task",
            "briefs. Dense, machine-to-machine, no padding.",
          ].join(" "),
        ),
    },
    async (args) => {
      const callerAgent = agentManager.getAgentSnapshot(callerAgentId);
      if (!callerAgent) {
        return {
          isError: true,
          content: [{ type: "text", text: `Caller agent ${callerAgentId} not found` }],
        };
      }

      const trimmedBrief = args.brief.trim();
      const callerTitle = callerAgent.config?.title ?? callerAgentId;

      const briefExcerpt =
        trimmedBrief.length > 240 ? `${trimmedBrief.slice(0, 237).trimEnd()}…` : trimmedBrief;
      const envelope = `<appostle-system kind="chain-started" title="${escapeAppostleSystemAttr(callerTitle)}">\n${briefExcerpt}\n</appostle-system>`;

      try {
        agentManager.recordUserMessage(callerAgentId, envelope, { emitState: false });
      } catch (err) {
        log.error({ err }, "chain: failed to record breadcrumb envelope");
      }

      return {
        content: [
          {
            type: "text",
            text: withChainContract(trimmedBrief),
          },
        ],
      };
    },
  );

  // ── learn ─────────────────────────────────────────────────────────────────
  server.tool(
    "learn",
    [
      "Explain the last suggestions, edits, or outcome in simpler plain English.",
      "Use this when the user sends a message starting with `>learn`, or when",
      "the user taps the graduation-cap composer action.",
      "",
      "After this tool returns, answer in plain language only. Help the user",
      "catch up on what is happening here without re-summarising the entire",
      "conversation. Use one short paragraph by default. If the explanation",
      "needs structure, add a few bullets, but do not make it a giant summary.",
      "No code, no file references, no technical repeat.",
    ].join("\n"),
    {},
    async () => {
      return {
        content: [
          {
            type: "text",
            text: [
              "Now answer the user's `>learn` request directly.",
              "Use simpler plain language only. Explain the last suggestions, edits, or outcome.",
              "Keep it short, and do not re-summarise the whole conversation.",
            ].join(" "),
          },
        ],
      };
    },
  );

  // ── write_plan ────────────────────────────────────────────────────────────
  server.tool(
    "write_plan",
    WRITE_PLAN_DESCRIPTION,
    writePlanSchema,
    buildWritePlanHandler(callerAgentId, { agentManager, logger }),
  );

  // ── todo ──────────────────────────────────────────────────────────────────
  server.tool(
    "todo",
    [
      "Create or extend an item on the user's project-scoped Todo list,",
      "shown in a dropdown in the workspace top bar. Use this ONLY when the",
      "user sends a message starting with `>todo`.",
      "",
      "Act immediately. Do NOT ask the user for confirmation before creating",
      "the todo. Do NOT ask clarifying questions. Parse what they sent, decide",
      "the structure yourself, and call this tool. Only use AskUserQuestion if",
      "there is GENUINE structural ambiguity that cannot be resolved from",
      "context — e.g. two equally plausible decompositions that would produce",
      "different todos. The bar is high; default to acting.",
      "",
      "If the user attaches a file or references a path alongside `>todo`,",
      "read it silently as part of this tool call. The file IS the context for",
      "the todo — not a separate task to discuss first.",
      "",
      "Synthesize, don't transcribe. Read the user's freeform `>todo` message,",
      "decide what the todo actually is, and split it into structured fields:",
      "  - `title` — short actionable headline (≤200 chars).",
      "  - `notes` — optional markdown body for long-form context (no cap).",
      "  - `subtasks` — optional ordered concrete steps (each ≤200 chars).",
      "Prefer pushing length into `notes` over a long title. Title and subtask",
      "items should read as short labels; explanation goes in `notes`.",
      "",
      "Smart-merge: if the user's `>todo` reads like additional subtasks for a",
      "todo that already exists in this workspace, FIRST call `todo_list` to",
      "see the open todos, pick the matching parent's id, and pass it as",
      "`mergeIntoTodoId`. Your `subtasks` will then be appended under that",
      "parent (its title + notes stay untouched). When the connection is not",
      "clearly the same task, omit `mergeIntoTodoId` and create a fresh todo.",
      "",
      "Fallback: if you genuinely cannot decide the structure, pass the user's",
      "verbatim message as `markdown` and the daemon will parse it best-effort.",
      "Prefer the structured path — the fallback is for ambiguous edge cases.",
      "",
      "After the tool returns, DO NOT summarize what you created, do NOT list",
      "the title/notes/subtasks back to the user, do NOT narrate. The dropdown",
      "shows the todo. Reply with at most a one-line acknowledgement, or",
      "nothing at all if the next user turn naturally continues.",
    ].join("\n"),
    {
      title: z
        .string()
        .min(1)
        .max(200)
        .optional()
        .describe(
          "Short actionable headline (≤200 chars). Required unless `markdown` is provided.",
        ),
      notes: z
        .string()
        .optional()
        .describe("Optional markdown body for long-form context. No length cap."),
      subtasks: z
        .array(z.string().min(1).max(200))
        .optional()
        .describe("Optional ordered list of concrete subtasks, each ≤200 chars."),
      mergeIntoTodoId: z
        .string()
        .optional()
        .describe(
          "If set, `subtasks` are appended under the existing todo with this id (parent title + notes untouched). Omit to create a new todo.",
        ),
      markdown: z
        .string()
        .optional()
        .describe(
          "Fallback raw markdown for the daemon to parse when you can't synthesize structure. Ignored if `title` is provided.",
        ),
    },
    async (args) => {
      if (!addTodoFromAgent) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "todo: the daemon is not configured with a todo store.",
            },
          ],
        };
      }
      if (!args.title && !args.markdown) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "todo: must provide either `title` (structured) or `markdown` (fallback).",
            },
          ],
        };
      }
      const parentAgent = agentManager.getAgentSnapshot(callerAgentId);
      if (!parentAgent) {
        return {
          isError: true,
          content: [{ type: "text", text: `Parent agent ${callerAgentId} not found` }],
        };
      }
      const result = await addTodoFromAgent({
        cwd: parentAgent.cwd,
        agentId: callerAgentId,
        title: args.title,
        notes: args.notes,
        subtasks: args.subtasks,
        mergeIntoTodoId: args.mergeIntoTodoId,
        markdown: args.markdown,
      });
      if (!result.ok) {
        const errorText =
          result.error === "no_remote"
            ? "todo not added \u2014 this workspace has no git remote. Set one with `git remote add origin <url>` and try again."
            : `todo: ${result.error}`;
        return {
          isError: true,
          content: [{ type: "text", text: errorText }],
        };
      }
      const msg =
        result.mode === "merged"
          ? "Appended subtasks under the existing todo. Open the Todo dropdown in the workspace top bar to see it."
          : "Added to your project Todo list. Open the Todo dropdown in the workspace top bar to see it.";
      return {
        content: [{ type: "text", text: msg }],
      };
    },
  );

  // ── todo_list ─────────────────────────────────────────────────────────────
  server.tool(
    "todo_list",
    [
      "List the user's currently-OPEN todos for the workspace the calling",
      "agent runs in. Returns an array of `{ id, title, subtasks[] }`.",
      "",
      "Call this from inside a `>todo` handler when the user's message could",
      "plausibly be additional work for an existing todo (e.g. it references",
      "the same noun, or reads as 'also do X for that thing'). Pick the",
      "matching parent and pass its id as `mergeIntoTodoId` on `todo`.",
      "",
      "Skip this call when the `>todo` clearly stands alone — the default is",
      "to create a fresh todo. There is no need to call `todo_list` for every",
      "`>todo`; only when smart-merge is genuinely possible.",
    ].join("\n"),
    {},
    async () => {
      if (!listTodosForAgent) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "todo_list: the daemon is not configured with a todo store.",
            },
          ],
        };
      }
      const parentAgent = agentManager.getAgentSnapshot(callerAgentId);
      if (!parentAgent) {
        return {
          isError: true,
          content: [{ type: "text", text: `Parent agent ${callerAgentId} not found` }],
        };
      }
      const result = await listTodosForAgent({ cwd: parentAgent.cwd });
      if (!result.ok) {
        const errorText =
          result.error === "no_remote"
            ? "todo_list: this workspace has no git remote, so it has no todos yet."
            : `todo_list: ${result.error}`;
        return {
          isError: true,
          content: [{ type: "text", text: errorText }],
        };
      }
      return {
        content: [{ type: "text", text: JSON.stringify({ todos: result.todos }, null, 2) }],
      };
    },
  );

  // ── complete_todo ─────────────────────────────────────────────────────────
  server.tool(
    "complete_todo",
    [
      "Mark a todo as completed (tick its checkbox in the workspace Todo list).",
      "",
      "Call this when your session was spawned FROM a todo and the user has just",
      "confirmed — in this conversation — that the todo is done. The closing",
      "footer of your initial prompt told you the todoId; pass it back here.",
      "",
      "Do NOT call this on your own initiative without the user's confirmation,",
      "and do NOT call it for any todo other than the one that spawned you.",
      "",
      "After the tool returns, acknowledge briefly (one line max) — the user",
      "will see the todo flip to completed in their dropdown.",
      "",
      "Pass an optional `report` summarizing what was done — it is stored as",
      "`result_report` on the todo and surfaced in the Details pane.",
    ].join("\n"),
    {
      todoId: z.string().min(1).describe("The id of the todo to mark completed."),
      report: z.string().optional().describe("Optional closing report summarizing what was done."),
    },
    async (args) => {
      if (!completeTodoFromAgent) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "complete_todo: the daemon is not configured with a todo store.",
            },
          ],
        };
      }
      const parentAgent = agentManager.getAgentSnapshot(callerAgentId);
      if (!parentAgent) {
        return {
          isError: true,
          content: [{ type: "text", text: `Parent agent ${callerAgentId} not found` }],
        };
      }
      const result = await completeTodoFromAgent({
        cwd: parentAgent.cwd,
        todoId: args.todoId,
        report: args.report ?? undefined,
        agentId: callerAgentId,
      });
      if (!result.ok) {
        const errorText =
          result.error === "no_remote"
            ? "complete_todo: this workspace has no git remote, so it has no todos."
            : `complete_todo: ${result.error}`;
        return {
          isError: true,
          content: [{ type: "text", text: errorText }],
        };
      }
      return {
        content: [{ type: "text", text: "Marked the todo as completed." }],
      };
    },
  );

  // ── cross_hosts_terminal ──────────────────────────────────────────────────
  // Run a command on a SIBLING host's shell over the relay — the same full-trust
  // shell the in-app terminal grants org members, driven from this machine.
  // Only registered when the owner has a reachable sibling; the description
  // lists exactly which hosts are reachable right now.
  if (crossHostExec && crossHostExec.reachableSiblings.length > 0) {
    const siblings = crossHostExec.reachableSiblings;
    const siblingList = siblings
      .map((s) => {
        const org = s.organization?.name ? `, org: ${s.organization.name}` : "";
        const owner = s.owner?.handle
          ? `, owner: ${s.owner.handle}`
          : s.owner?.userId
            ? `, ownerUserId: ${s.owner.userId}`
            : "";
        return `- \`${s.name}\` (serverId: ${s.serverId}${org}${owner})`;
      })
      .join("\n");
    server.tool(
      "cross_hosts_terminal",
      [
        "Run a shell command on a SIBLING host you share access to. This is",
        "your normal Bash-tool behavior, pointed at a different machine —",
        "non-interactive: you get clean stdout, stderr, and an exit code back.",
        "Use it for recon, reads, builds, or scripted actions on a sibling host.",
        "The Appostle tool layer first resolves the best route for this target:",
        "working SSH profile, LAN SSH, Appostle direct E2EE, then Appostle relay",
        "E2EE. Do not decide between those routes yourself.",
        "Use this when the user asks to inspect or operate on another named",
        "Appostle machine, even when they do not type the directive explicitly.",
        "",
        "It runs the owner's login shell (`<shell> -lc <command>`), the same",
        "full-trust shell the in-app terminal grants. There is no sandbox —",
        "treat it like a terminal on your own host.",
        "",
        "Bounds: output is capped at 1 MB and the command is killed after 120s.",
        "",
        "Reachable sibling hosts right now:",
        siblingList,
        "",
        "Pass `host` as either the host name or its serverId from the list above.",
      ].join("\n"),
      {
        host: z
          .string()
          .min(1)
          .describe(
            "Target sibling host — its name or serverId from the list in this tool's description.",
          ),
        command: z.string().min(1).describe("The shell command to run on the sibling host."),
        cwd: z
          .string()
          .optional()
          .describe("Working directory for the command. Defaults to the owner's home directory."),
      },
      async (args) => {
        const query = args.host.trim();
        const matches = siblings.filter(
          (s) => s.serverId === query || s.name.toLowerCase() === query.toLowerCase(),
        );
        if (matches.length === 0) {
          return {
            isError: true,
            content: [
              {
                type: "text",
                text: `cross_hosts_terminal: no reachable sibling matches "${query}". Reachable: ${siblings
                  .map((s) => s.name)
                  .join(", ")}.`,
              },
            ],
          };
        }
        if (matches.length > 1) {
          return {
            isError: true,
            content: [
              {
                type: "text",
                text: `cross_hosts_terminal: "${query}" is ambiguous (matches ${matches.length} hosts). Pass the serverId instead: ${matches
                  .map((s) => s.serverId)
                  .join(", ")}.`,
              },
            ],
          };
        }
        const target = matches[0];
        const progress: CrossHostProgressState = {
          operation: "terminal",
          target,
          command: args.command,
          cwd: args.cwd,
          steps: [
            `Resolved "${query}" to ${target.name} (${target.serverId}).`,
            target.organization?.name ? `Organization: ${target.organization.name}.` : "",
            target.owner?.handle || target.owner?.userId
              ? `Owner: ${target.owner.handle ?? target.owner.userId}.`
              : "",
          ].filter(Boolean),
        };
        const progressCallId = `appostle-cross-host-terminal-${target.serverId}-${randomUUID()}`;
        const emitProgress = () => {
          emitCrossHostProgress(agentManager, callerAgentId, progressCallId, progress);
        };
        const addProgress = (message: string) => {
          progress.steps = [...progress.steps, message];
          emitProgress();
        };
        emitProgress();

        // Live output streaming: accumulate chunks onto the progress state and
        // re-emit at most every 250ms so long-running remote commands paint
        // incrementally without flooding the timeline pipe.
        const STREAM_EMIT_INTERVAL_MS = 250;
        let lastStreamEmit = 0;
        let streamEmitTimer: NodeJS.Timeout | null = null;
        const onExecChunk = (stream: "stdout" | "stderr", text: string) => {
          progress[stream] = capCrossHostOutput((progress[stream] ?? "") + text);
          const now = Date.now();
          if (now - lastStreamEmit >= STREAM_EMIT_INTERVAL_MS) {
            lastStreamEmit = now;
            emitProgress();
          } else if (!streamEmitTimer) {
            streamEmitTimer = setTimeout(() => {
              streamEmitTimer = null;
              lastStreamEmit = Date.now();
              emitProgress();
            }, STREAM_EMIT_INTERVAL_MS);
          }
        };
        const stopStreamEmits = () => {
          if (streamEmitTimer) {
            clearTimeout(streamEmitTimer);
            streamEmitTimer = null;
          }
        };

        try {
          const lookup = createAuthServerLookup({
            logger: log,
            authServerUrl: crossHostExec.authServerUrl,
            daemonToken: crossHostExec.daemonToken,
          });
          addProgress("Fetching current pairing offer and LAN endpoint metadata.");
          const peer = await lookup.fetchPeerOffer(target.serverId);
          const route = await resolveCrossHostRoute(
            {
              name: target.name,
              serverId: target.serverId,
              directEndpoints: peer.directEndpoints,
            },
            undefined,
            (event: CrossHostRouteProgressEvent) => {
              if (event.route) {
                progress.route = event.route;
              }
              progress.steps = [...progress.steps, event.message];
              emitProgress();
            },
          );
          progress.route = route.kind;
          progress.routeReason = route.reason;
          emitProgress();
          if (route.kind === "ssh-setup-needed") {
            emitCrossHostProgress(agentManager, callerAgentId, progressCallId, progress, "failed", {
              message: route.reason,
            });
            return {
              isError: true,
              content: [
                {
                  type: "text",
                  text: [
                    ...formatCrossHostIdentityLines(target),
                    "route: ssh-setup-needed",
                    `route_reason: ${route.reason}`,
                    "",
                    `SSH is reachable on ${route.setupHost ?? target.name}, but this machine does not have non-interactive SSH auth for it yet.`,
                    "Ask the user for approval before setting up access. Do not store passwords in Appostle; prefer an SSH key or OS keychain-backed credential flow.",
                    "Needed inputs: SSH username, whether to install/reuse an SSH public key, and whether sudo is allowed on the target.",
                  ].join("\n"),
                },
              ],
            };
          }
          if (route.kind === "ssh" && route.sshHost) {
            addProgress(`Running command over SSH route ${route.sshHost}.`);
            const result = await runSshExec({
              sshHost: route.sshHost,
              command: args.command,
              cwd: args.cwd,
              onChunk: onExecChunk,
            });
            stopStreamEmits();
            progress.exit =
              result.code === null ? `signal ${result.signal ?? "?"}` : String(result.code);
            progress.stdout = capCrossHostOutput(result.stdout);
            progress.stderr = capCrossHostOutput(result.stderr);
            emitCrossHostProgress(
              agentManager,
              callerAgentId,
              progressCallId,
              progress,
              result.code !== null && result.code !== 0 ? "failed" : "completed",
              result.error ?? undefined,
            );
            const parts: string[] = [];
            parts.push(...formatCrossHostIdentityLines(target));
            parts.push("route: ssh");
            parts.push(`route_reason: ${route.reason}`);
            parts.push(
              `exit: ${result.code === null ? `signal ${result.signal ?? "?"}` : result.code}`,
            );
            if (result.error) parts.push(`note: ${result.error}`);
            parts.push("");
            parts.push("--- stdout ---");
            parts.push(result.stdout.length > 0 ? result.stdout : "(empty)");
            parts.push("--- stderr ---");
            parts.push(result.stderr.length > 0 ? result.stderr : "(empty)");
            return {
              isError: result.code !== null && result.code !== 0,
              content: [{ type: "text", text: parts.join("\n") }],
            };
          }
          addProgress("Minting relay token for Appostle E2EE transport.");
          const { token: wsToken } = await lookup.mintWsToken({
            device: crossHostExec.clientIdentityPubkeyB64,
            hostServerId: peer.serverId,
          });
          addProgress("Dialing target daemon over Appostle E2EE transport.");
          const result = await dialHostExec({
            logger: log,
            clientIdentity: crossHostExec.clientIdentity,
            clientIdentityPubkeyB64: crossHostExec.clientIdentityPubkeyB64,
            clientId: `host-exec-${Math.random().toString(36).slice(2, 10)}`,
            peer,
            wsToken,
            exec: { command: args.command, cwd: args.cwd },
            onChunk: onExecChunk,
          });
          stopStreamEmits();
          progress.route = `appostle-${result.transport}`;
          progress.exit =
            result.code === null ? `signal ${result.signal ?? "?"}` : String(result.code);
          progress.stdout = capCrossHostOutput(result.stdout);
          progress.stderr = capCrossHostOutput(result.stderr);
          emitCrossHostProgress(
            agentManager,
            callerAgentId,
            progressCallId,
            progress,
            result.code !== null && result.code !== 0 ? "failed" : "completed",
            result.error ?? undefined,
          );

          const parts: string[] = [];
          parts.push(...formatCrossHostIdentityLines(target));
          parts.push(`route: appostle-${result.transport}`);
          parts.push(`route_reason: ${route.reason}`);
          parts.push(
            `exit: ${result.code === null ? `signal ${result.signal ?? "?"}` : result.code}`,
          );
          if (result.error) parts.push(`note: ${result.error}`);
          parts.push("");
          parts.push("--- stdout ---");
          parts.push(result.stdout.length > 0 ? result.stdout : "(empty)");
          parts.push("--- stderr ---");
          parts.push(result.stderr.length > 0 ? result.stderr : "(empty)");

          return {
            isError: result.code !== null && result.code !== 0,
            content: [{ type: "text", text: parts.join("\n") }],
          };
        } catch (err) {
          stopStreamEmits();
          progress.steps = [
            ...progress.steps,
            `Cross-host command failed: ${err instanceof Error ? err.message : String(err)}.`,
          ];
          emitCrossHostProgress(
            agentManager,
            callerAgentId,
            progressCallId,
            progress,
            "failed",
            err instanceof Error ? { message: err.message } : { message: String(err) },
          );
          log.warn({ err, target: target.serverId }, "cross_hosts_terminal failed");
          return {
            isError: true,
            content: [
              {
                type: "text",
                text: `cross_hosts_terminal: failed to run on ${target.name} — ${err instanceof Error ? err.message : String(err)}`,
              },
            ],
          };
        }
      },
    );

    // ── cross_hosts_transfer ─────────────────────────────────────────────────
    // Move one file between this host and a sibling over the same encrypted
    // relay channel the terminal uses. Chunked, ack-windowed, sha256-verified
    // on the receiving side — works from anywhere the relay is reachable.
    server.tool(
      "cross_hosts_transfer",
      [
        "Transfer a single file between THIS host and a SIBLING host. The tool",
        "first resolves the best route for this target: working SSH profile, LAN",
        "SSH, Appostle direct E2EE, then Appostle relay E2EE. Appostle transfer",
        "verifies a sha256 of the whole file before reporting success, and writes",
        "via a temp file + atomic rename (no partial files on failure).",
        "Use this when the user sends a message starting with `>cross_hosts_transfer`.",
        "",
        "direction `pull` copies sibling:remote_path → local_path on this host.",
        "direction `push` copies local_path on this host → sibling:remote_path.",
        "Paths may use `~/`; relative paths resolve against the daemon cwd, so",
        "prefer absolute or `~/` paths. Existing destination files are",
        "overwritten. Cap: 256 MiB per file.",
        "",
        "Reachable sibling hosts right now:",
        siblingList,
        "",
        "Pass `host` as either the host name or its serverId from the list above.",
      ].join("\n"),
      {
        host: z
          .string()
          .min(1)
          .describe(
            "Target sibling host — its name or serverId from the list in this tool's description.",
          ),
        direction: z
          .enum(["pull", "push"])
          .describe("pull: sibling → this host. push: this host → sibling."),
        remote_path: z.string().min(1).describe("File path on the sibling host."),
        local_path: z.string().min(1).describe("File path on this host."),
      },
      async (args) => {
        const query = args.host.trim();
        const matches = siblings.filter(
          (s) => s.serverId === query || s.name.toLowerCase() === query.toLowerCase(),
        );
        if (matches.length === 0) {
          return {
            isError: true,
            content: [
              {
                type: "text",
                text: `cross_hosts_transfer: no reachable sibling matches "${query}". Reachable: ${siblings
                  .map((s) => s.name)
                  .join(", ")}.`,
              },
            ],
          };
        }
        if (matches.length > 1) {
          return {
            isError: true,
            content: [
              {
                type: "text",
                text: `cross_hosts_transfer: "${query}" is ambiguous (matches ${matches.length} hosts). Pass the serverId instead: ${matches
                  .map((s) => s.serverId)
                  .join(", ")}.`,
              },
            ],
          };
        }
        const target = matches[0];

        try {
          const lookup = createAuthServerLookup({
            logger: log,
            authServerUrl: crossHostExec.authServerUrl,
            daemonToken: crossHostExec.daemonToken,
          });
          const peer = await lookup.fetchPeerOffer(target.serverId);
          const route = await resolveCrossHostRoute({
            name: target.name,
            serverId: target.serverId,
            directEndpoints: peer.directEndpoints,
          });
          if (route.kind === "ssh-setup-needed") {
            return {
              isError: true,
              content: [
                {
                  type: "text",
                  text: [
                    `host: ${target.name} (${target.serverId})`,
                    "route: ssh-setup-needed",
                    `route_reason: ${route.reason}`,
                    "",
                    `SSH is reachable on ${route.setupHost ?? target.name}, but this machine does not have non-interactive SSH auth for it yet.`,
                    "Ask the user for approval before setting up access. Do not store passwords in Appostle; prefer an SSH key or OS keychain-backed credential flow.",
                    "Needed inputs: SSH username, whether to install/reuse an SSH public key, and whether sudo is allowed on the target.",
                  ].join("\n"),
                },
              ],
            };
          }
          if (route.kind === "ssh" && route.sshHost) {
            const result = await runSshTransfer({
              sshHost: route.sshHost,
              direction: args.direction,
              remotePath: args.remote_path,
              localPath: args.local_path,
            });
            const arrow =
              args.direction === "pull"
                ? `${target.name}:${args.remote_path} → ${args.local_path}`
                : `${args.local_path} → ${target.name}:${args.remote_path}`;
            return {
              isError: result.code !== null && result.code !== 0,
              content: [
                {
                  type: "text",
                  text: [
                    `host: ${target.name} (${target.serverId})`,
                    "route: ssh",
                    `route_reason: ${route.reason}`,
                    `transferred: ${arrow}`,
                    `exit: ${result.code === null ? `signal ${result.signal ?? "?"}` : result.code}`,
                    result.error ? `note: ${result.error}` : undefined,
                    result.stdout.trim() ? `stdout: ${result.stdout.trim()}` : undefined,
                    result.stderr.trim() ? `stderr: ${result.stderr.trim()}` : undefined,
                  ]
                    .filter((line): line is string => Boolean(line))
                    .join("\n"),
                },
              ],
            };
          }
          const { token: wsToken } = await lookup.mintWsToken({
            device: crossHostExec.clientIdentityPubkeyB64,
            hostServerId: peer.serverId,
          });
          const result = await dialHostTransfer({
            logger: log,
            clientIdentity: crossHostExec.clientIdentity,
            clientIdentityPubkeyB64: crossHostExec.clientIdentityPubkeyB64,
            clientId: `host-transfer-${Math.random().toString(36).slice(2, 10)}`,
            peer,
            wsToken,
            transfer: {
              direction: args.direction,
              remotePath: args.remote_path,
              localPath: args.local_path,
            },
          });

          const arrow =
            args.direction === "pull"
              ? `${target.name}:${args.remote_path} → ${args.local_path}`
              : `${args.local_path} → ${target.name}:${args.remote_path}`;
          return {
            content: [
              {
                type: "text",
                text: [
                  `host: ${target.name} (${target.serverId})`,
                  `route: appostle-${result.transport}`,
                  `route_reason: ${route.reason}`,
                  `transferred: ${arrow}`,
                  `${result.bytes} bytes, sha256 ${result.sha256} (verified by receiver).`,
                ].join("\n"),
              },
            ],
          };
        } catch (err) {
          log.warn({ err, target: target.serverId }, "cross_hosts_transfer failed");
          return {
            isError: true,
            content: [
              {
                type: "text",
                text: `cross_hosts_transfer: ${args.direction} failed on ${target.name} — ${err instanceof Error ? err.message : String(err)}`,
              },
            ],
          };
        }
      },
    );
  }

  return server;
}
